import {useSuspenseQuery} from '@tanstack/react-query'
import {PropsWithChildren, Suspense} from 'react'
import clsx from 'clsx'
import {productFilterQueryOptions} from '~/client/fn/productFilter'
import {collectionsQueryOptions} from '~/client/fn/collections'
import SubMenu from '~/components/navigation/SubMenu'
import useLocaleParams from '~/hooks/useLocaleParams'

type DesktopMenuProps = PropsWithChildren<{
    isVisible: boolean
    closeMenu: () => void
}>

const DesktopMenu = ({children, isVisible, closeMenu}: DesktopMenuProps) => (
    <div
        className={clsx(
            'absolute left-0 top-full w-full backdrop-blur-xl transform-gpu bg-black/70 transition-opacity',
            isVisible ? 'opacity-100' : 'opacity-0'
        )}
        // @ts-ignore
        inert={!isVisible ? 'true' : undefined}
        onMouseLeave={closeMenu}
    >
        <div className="border-t border-gray-500 mx-auto max-w-screen-xl w-full flex flex-row flex-wrap gap-14 justify-between pl-[calc(200px_+_4rem_+_1rem)] pr-4 sm:pr-6">
            <Suspense>{children}</Suspense>
        </div>
    </div>
)

type SubListProps = {
    closeMenu: () => void
    openMenu: () => void
    isVisible: boolean
}

export const DesignCollectionMenu = ({closeMenu, openMenu, isVisible}: SubListProps) => {
    const params = useLocaleParams()
    const {data: collections} = useSuspenseQuery(collectionsQueryOptions(params.locale))

    if (!collections?.length) {
        return null
    }

    return (
        <DesktopMenu isVisible={isVisible} closeMenu={closeMenu}>
            <SubMenu
                className="flex flex-row justify-center gap-1"
                onMouseEnter={openMenu}
                onClose={closeMenu}
                items={collections.map((collection) => ({
                    className:
                        'p-4 border-b-2 border-b-transparent hover:border-b-nova bg-gradient-to-t hover:from-nova/20 to-10% to-transparent',
                    id: collection?.id!,
                    to: '/$lang/collections/$collectionId',
                    params: {...params, collectionId: collection.id},
                    label: collection.name,
                }))}
            />
        </DesktopMenu>
    )
}

export const ProductDepartmentMenu = ({closeMenu, openMenu, isVisible}: SubListProps) => {
    const params = useLocaleParams()
    const {data: filters} = useSuspenseQuery(
        productFilterQueryOptions(
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            undefined,
            params.locale
        )
    )

    if (!filters?.categories?.length) {
        return null
    }

    return (
        <DesktopMenu isVisible={isVisible} closeMenu={closeMenu}>
            <SubMenu
                className="flex flex-row justify-center gap-1"
                onMouseEnter={openMenu}
                onClose={closeMenu}
                items={filters.categories.map((department) => ({
                    className:
                        'p-4 border-b-2 border-b-transparent hover:border-b-nova bg-gradient-to-t hover:from-nova/20 to-10% to-transparent',
                    id: department?.id!,
                    to: '/$lang/products',
                    params: params,
                    search: {department: department?.id ?? undefined},
                    label: department?.name!,
                }))}
            />
        </DesktopMenu>
    )
}
